// @ai-generated 2026-09-17T10:27:43+00:00 by niklas.eicker@tngtech.com using Claude Opus 5 (1M context)
import { type AssistantMessage, type Message, type ToolMessage, type UserMessage } from '@ag-ui/client';
import {
    CopilotChatAssistantMessage,
    CopilotChatMessageView,
    CopilotChatUserMessage,
    type ReactToolCallRenderer,
    ToolCallStatus,
} from '@copilotkit/react-core/v2';
import classes from './ChatMessageList.module.css';

/**
 * Binds the tool-call cards into a component the chat view accepts.
 */
export function createMessageView(renderToolCalls: ReactToolCallRenderer[]) {
    function ToolCallsView({ message, messages = [] }: { message: AssistantMessage; messages?: Message[] }) {
        return (
            <>
                {message.toolCalls?.map((toolCall) => {
                    const renderer =
                        renderToolCalls.find((candidate) => candidate.name === toolCall.function.name) ??
                        renderToolCalls.find((candidate) => candidate.name === '*');
                    if (!renderer) {
                        return null;
                    }

                    const answer = messages.find(
                        (candidate): candidate is ToolMessage =>
                            candidate.role === 'tool' && candidate.toolCallId === toolCall.id,
                    );

                    const Render = renderer.render;
                    const shared = {
                        name: toolCall.function.name,
                        toolCallId: toolCall.id,
                        args: parseArguments(toolCall.function.arguments),
                    };

                    return answer ? (
                        <Render
                            key={toolCall.id}
                            {...shared}
                            status={ToolCallStatus.Complete}
                            result={answer.content}
                        />
                    ) : (
                        <Render key={toolCall.id} {...shared} status={ToolCallStatus.InProgress} result={undefined} />
                    );
                })}
            </>
        );
    }

    function MessageList({ messages = [], isRunning = false }: { messages?: Message[]; isRunning?: boolean }) {
        return (
            <div className={classes.list}>
                {messages.map((message) => {
                    if (message.role === 'user') {
                        return <CopilotChatUserMessage key={message.id} message={message as UserMessage} />;
                    }
                    if (message.role !== 'assistant') {
                        return null;
                    }
                    return (
                        <CopilotChatAssistantMessage
                            key={message.id}
                            message={message as AssistantMessage}
                            messages={messages}
                            toolCallsView={ToolCallsView}
                        />
                    );
                })}
                {isRunning && <div className={classes.cursor} />}
            </div>
        );
    }

    return Object.assign(MessageList, { Cursor: CopilotChatMessageView.Cursor });
}

function parseArguments(serialized: string): Record<string, unknown> {
    try {
        return JSON.parse(serialized) as Record<string, unknown>;
    } catch {
        // A call still streaming its arguments is not valid JSON yet; the cards read defensively.
        return {};
    }
}
